import moment from 'moment';
import { uniqBy, every } from 'lodash-es';
import { API_DATE_FORMAT, AVAILABLE_LANGUAGES, surveyFieldIds } from '../constants';
import { surveyTypeValues } from '../constants/types';
import { isSurveyForDegreeProgram } from '../util';

const validateParticipants = (surveyOptions) =>
  Array.isArray(surveyOptions.participants) && (surveyOptions.participants.length || surveyOptions.courses.length)
  && !surveyOptions.participants.some(
    (participant) => !participant.email || (!participant.name && !participant.commonName && !participant.firstName && !participant.lastName)
  ) && uniqBy(surveyOptions.participants, 'email').length === surveyOptions.participants.length;

const validateSchema = (surveyOptions) =>
  surveyOptions.schemaId && Number.isInteger(surveyOptions.schemaId);

export const validateLocalizedNotEmpty = (data) =>
  (data) && (AVAILABLE_LANGUAGES.some((lang) => data[lang] && data[lang].length > 0));

const validateStartDate = (surveyOptions) =>
  surveyOptions.startDate
  && moment(surveyOptions.startDate, API_DATE_FORMAT).isValid();

const validateEndDate = (surveyOptions) =>
  surveyOptions.endDate
  && surveyOptions.startDate
  && moment(surveyOptions.endDate, API_DATE_FORMAT)
    .isAfter(moment(surveyOptions.startDate, API_DATE_FORMAT))
  && moment(surveyOptions.endDate, API_DATE_FORMAT).isValid();

const validateReminderTitle = (surveyOptions) =>
  (isSurveyForDegreeProgram(surveyOptions.surveyType)
    ? validateLocalizedNotEmpty(surveyOptions.reminderEmailTitle)
    : true);

const validateReminderContent = (surveyOptions) =>
  (isSurveyForDegreeProgram(surveyOptions.surveyType)
    ? validateLocalizedNotEmpty(surveyOptions.reminderEmailContent)
    : true);

const validateDegreeProgram = (surveyOptions) => (!!surveyOptions.degreeProgram);

const validateReminderSchedules = ({
  surveyType, reminderSchedules, endDate, startDate
}) =>
  surveyType !== surveyTypeValues.DEGREE_PROGRAM_SURVEY
  || every(reminderSchedules, (schedule) =>
    schedule !== ''
    && moment(schedule).isBefore(moment(endDate))
    && moment(schedule).isAfter(moment(startDate)));

const validateSurveyOptionField = (fieldId, surveyOptions) => {
  switch (fieldId) {
    case surveyFieldIds.TITLE: {
      return validateLocalizedNotEmpty(surveyOptions.title);
    }
    case surveyFieldIds.PARTICIPANTS_INPUT: {
      return validateParticipants(surveyOptions);
    }
    case surveyFieldIds.SCHEMA: {
      return validateSchema(surveyOptions);
    }
    case surveyFieldIds.INVITATION_TITLE: {
      return validateLocalizedNotEmpty(surveyOptions.invitationEmailTitle);
    }
    case surveyFieldIds.INVITATION_CONTENT: {
      return validateLocalizedNotEmpty(surveyOptions.invitationEmailContent);
    }
    case surveyFieldIds.REMINDER_TITLE: {
      return validateReminderTitle(surveyOptions);
    }
    case surveyFieldIds.REMINDER_CONTENT: {
      return validateReminderContent(surveyOptions);
    }
    case surveyFieldIds.START_DATE: {
      return validateStartDate(surveyOptions);
    }
    case surveyFieldIds.END_DATE: {
      return validateEndDate(surveyOptions);
    }
    case surveyFieldIds.ENDING_TITLE: {
      return surveyOptions.surveyType !== surveyTypeValues.COURSE_SURVEY
        ? validateLocalizedNotEmpty(surveyOptions.endingEmailTitle)
        : true;
    }
    case surveyFieldIds.ENDING_CONTENT: {
      return surveyOptions.surveyType !== surveyTypeValues.COURSE_SURVEY
        ? validateLocalizedNotEmpty(surveyOptions.endingEmailContent)
        : true;
    }
    case surveyFieldIds.DEGREE_PROGRAM_CODE: {
      // This is only mandatory for HUL surveys
      if (surveyOptions.surveyType === surveyTypeValues.DEGREE_PROGRAM_SURVEY) {
        return validateDegreeProgram(surveyOptions);
      }
      return true;
    }
    case surveyFieldIds.REMINDER_SCHEDULES: {
      return validateReminderSchedules(surveyOptions);
    }
    default: return true;
  }
};

const validate = (fields, data) => {
  const invalidFields = fields.filter(
    (fieldId) => !validateSurveyOptionField(fieldId, data)
  );
  return {
    invalidFields,
    isValid: invalidFields.length === 0
  };
};

export const schemaPageValidator = (data) => validate([surveyFieldIds.SCHEMA], data.surveyOptions);

export const contentsPageValidator = (data) =>
  validate([surveyFieldIds.TITLE, surveyFieldIds.DEGREE_PROGRAM_CODE], data.surveyOptions);

export const reopenSurveyPageValidator = (data) =>
  validate([surveyFieldIds.START_DATE, surveyFieldIds.END_DATE,
    surveyFieldIds.INVITATION_TITLE, surveyFieldIds.INVITATION_CONTENT,
    surveyFieldIds.ENDING_TITLE, surveyFieldIds.ENDING_CONTENT], data.surveyOptions);

export const surveySchedulingValidator = (data) =>
  validate([surveyFieldIds.START_DATE, surveyFieldIds.END_DATE, surveyFieldIds.REMINDER_SCHEDULES],
    data.surveyOptions);

export const surveyParticipantValidator = (data) =>
  validate([surveyFieldIds.PARTICIPANTS_INPUT], data.surveyOptions);

export const surveyReminderValidator = (data) =>
  validate([surveyFieldIds.REMINDER_TITLE, surveyFieldIds.REMINDER_CONTENT], data.surveyOptions);

export const rescheduleSurveyPageValidator = (data) =>
  validate([surveyFieldIds.END_DATE,
    surveyFieldIds.REMINDER_TITLE, surveyFieldIds.REMINDER_CONTENT], data.surveyOptions);

export const closeSurveyPageValidator = (data) =>
  validate([surveyFieldIds.ENDING_TITLE, surveyFieldIds.ENDING_CONTENT],
    {
      ...data.surveyOptions,
      surveyType: data.type
    });
